import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import {
  FILTER_KEYS, FILTER_LABELS, FILTER_DESCRIPTIONS, FILTER_ICONS, buildFilterSchema, emptyConfig,
  REFRESH_INTERVAL_OPTIONS, snapRefreshInterval,
} from './dashboardGlobalFilters'
import type { FilterBarConfig, FilterKey, FilterOptions } from './dashboardGlobalFilters'
import { roleKey, userKey, userIdsFromKeys, mergeRolePermissions } from './dashboardAccess'
import type { RoleOption, AllowedRole } from './dashboardAccess'

interface User { id: number; first_name?: string; last_name?: string; email?: string }
interface AllowedUser { id: number; can_read: boolean; can_write: boolean }

interface DashboardSettingsPayload {
  name: string
  description: string
  refresh_enabled: boolean
  refresh_interval_minutes: number
  accessible_by_all_users: boolean
  allowed_users: AllowedUser[]
  allowed_roles: AllowedRole[]
  filter_bar: FilterBarConfig
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DashboardSettingsDrawer',
  props: {
    show: { type: Boolean },
    dashboard: null,
    users: null,
    viewers: null,
    editors: null,
    roleOptions: null,
    allowedRoles: null,
    filterConfig: null,
    filterOptions: null,
    loading: { type: Boolean }
  },
  emits: ["close", "save"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'close'): void
  (e: 'save', payload: DashboardSettingsPayload): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  show: boolean
  dashboard: { name?: string; description?: string; refresh_enabled?: any; refresh_interval_minutes?: any; accessible_by_all_users?: any } | null
  users: User[]
  viewers: User[]
  editors: User[]
  roleOptions?: RoleOption[]
  allowedRoles?: AllowedRole[]
  filterConfig: FilterBarConfig
  filterOptions: FilterOptions | null
  loading?: boolean
}





// One entry per user id: the group's user list can repeat a user, and a selected viewer or
// editor may no longer be in that list (moved group), so they are merged in to keep a label.
function uniqueById(lists: User[][]): User[] {
  const seen = new Map<number, User>()
  for (const list of lists) for (const u of list || []) if (!seen.has(u.id)) seen.set(u.id, u)
  return Array.from(seen.values())
}

const userOptions = computed<User[]>(() => uniqueById([props.users, props.viewers, props.editors]))

// Roles sit above users in both selects; a selected role's chip reads "<name> (role)".
const roleSelectOptions = computed(() => (props.roleOptions || []).map((r) => ({ key: roleKey(r), name: r.name, chipLabel: r.name + ' (role)' })))

function freshDraft() {
  const d = props.dashboard || {}
  return {
    name: d.name || '',
    description: d.description || '',
    refresh_enabled: Boolean(d.refresh_enabled),
    refresh_interval_minutes: snapRefreshInterval(d.refresh_interval_minutes),
    accessible_by_all_users: Boolean(d.accessible_by_all_users),
    viewerKeys: [
      ...(props.allowedRoles || []).filter((r) => r.can_read).map(roleKey),
      ...uniqueById([props.viewers]).map((u) => userKey(u.id)),
    ],
    editorKeys: [
      ...(props.allowedRoles || []).filter((r) => r.can_write).map(roleKey),
      ...uniqueById([props.editors]).map((u) => userKey(u.id)),
    ],
    filterBar: JSON.parse(JSON.stringify(props.filterConfig || emptyConfig())) as FilterBarConfig,
  }
}

const draft = ref(freshDraft())
const open = ref({ name: true, filters: true, access: true, refresh: true })
const REFRESH_INTERVAL_TOGGLE_OPTIONS = REFRESH_INTERVAL_OPTIONS.map(o => ({ value: o.value, label: o.value === 60 ? '1 hour' : o.value + ' min' }))

// Description shows only while a tile is hovered; the reserved line height keeps the grid from jumping.
const describedKey = ref<FilterKey | null>(null)

function toggleKey(k: FilterKey) {
  const keys = draft.value.filterBar.keys
  draft.value.filterBar.keys = keys.includes(k) ? keys.filter(x => x !== k) : [...keys, k]
}
const errs = ref({ name: '' })

function userLabel(u: User): string {
  if (u.first_name && u.last_name) return u.first_name + ' ' + u.last_name
  if (u.first_name) return u.first_name
  if (u.last_name) return u.last_name
  return u.email || ''
}

// Mirrors the merge the editor used to do: a user in both lists gets both flags.
function mergePermissions(viewers: User[], editors: User[]): AllowedUser[] {
  const allowed: Record<number, AllowedUser> = {}
  for (const u of viewers) {
    if (!allowed[u.id]) allowed[u.id] = { id: u.id, can_read: true, can_write: false }
    else allowed[u.id].can_read = true
  }
  for (const u of editors) {
    if (!allowed[u.id]) allowed[u.id] = { id: u.id, can_read: false, can_write: true }
    else allowed[u.id].can_write = true
  }
  return Object.values(allowed)
}

// Keys are stored in registry order; the checklist can't reorder them.
const orderedKeys = computed<FilterKey[]>(() => FILTER_KEYS.filter(k => draft.value.filterBar.keys.includes(k)))

const schema = computed(() => {
  if (!props.filterOptions) return []
  return buildFilterSchema(orderedKeys.value, draft.value.filterBar.defaults, props.filterOptions)
})

function onDefaultsChange(values: Record<string, any>) {
  draft.value.filterBar.defaults = { ...draft.value.filterBar.defaults, ...values }
}

function validate(): boolean {
  errs.value.name = draft.value.name.trim() === '' ? 'Dashboard name cannot be empty' : ''
  return !errs.value.name
}

function onSave() {
  if (!validate()) return
  const defaults: Record<string, any> = {}
  for (const k of orderedKeys.value) if (draft.value.filterBar.defaults[k] !== undefined) defaults[k] = draft.value.filterBar.defaults[k]
  if (orderedKeys.value.includes('period') && draft.value.filterBar.defaults.custom_dates) defaults.custom_dates = draft.value.filterBar.defaults.custom_dates
  emit('save', {
    name: draft.value.name.trim(),
    description: draft.value.description,
    refresh_enabled: draft.value.refresh_enabled,
    refresh_interval_minutes: snapRefreshInterval(draft.value.refresh_interval_minutes),
    accessible_by_all_users: draft.value.accessible_by_all_users,
    allowed_users: mergePermissions(
      userIdsFromKeys(draft.value.viewerKeys).map((id) => ({ id })),
      userIdsFromKeys(draft.value.editorKeys).map((id) => ({ id })),
    ),
    allowed_roles: mergeRolePermissions(draft.value.viewerKeys, draft.value.editorKeys),
    filter_bar: { enabled: draft.value.filterBar.enabled, keys: orderedKeys.value, defaults },
  })
}

function onClose() {
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.show) onClose()
}

watch(
  () => props.show,
  (isOpen) => {
    if (isOpen) {
      draft.value = freshDraft()
      errs.value = { name: '' }
      open.value = { name: true, filters: true, access: true, refresh: true }
      document.addEventListener('keydown', onEscKey)
    } else {
      document.removeEventListener('keydown', onEscKey)
    }
  },
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})

return { __sfc: true,props, emit, uniqueById, userOptions, roleSelectOptions, freshDraft, draft, open, REFRESH_INTERVAL_TOGGLE_OPTIONS, describedKey, toggleKey, errs, userLabel, mergePermissions, orderedKeys, schema, onDefaultsChange, validate, onSave, onClose, onEscKey, FILTER_KEYS, FILTER_LABELS, FILTER_DESCRIPTIONS, FILTER_ICONS, userKey }
}

})